import { createContext, useCallback, useContext, useState } from 'react';

const EntitiesContext = createContext();

export const EntitiesProvider = ({ children }) => {
  const [error, setError] = useState(null);
  const [quote, setQuote] = useState({ id: null, properties: [] });

  const quoteID = quote.id;

  const setQuoteId = useCallback(
    (NewID) => {
      try {
        const mismatchID = quote.id && quote.id !== NewID;
        if (mismatchID)
          throw new Error('Tried to add quoteId with an existant different ID');
        setQuote((previousQuote) => ({ ...previousQuote, id: NewID }));
      } catch (error) {
        setError(error);
        console.error(error);
      }
    },
    [setQuote, quote]
  );

  const updateQuoteProperties = useCallback(
    (newProperties) => {
      const treatedNewProperties = newProperties.map((newProperty) => {
        const { id, namespace, data, options } = newProperty;
        const treatedProperty = { id, namespace, data };
        const treatedOptions =
          options?.map(({ data, label, id }, index) => ({
            id: id ?? index,
            data,
            label,
          })) ?? [];
        if (treatedOptions.length > 0) treatedProperty.options = treatedOptions;
        return treatedProperty;
      });
      setQuote((previousQuote) => {
        const oldProperties = previousQuote.properties;
        const propertiesToUpdate = treatedNewProperties.filter(
          ({ namespace, data: newData }) => {
            const oldMatch = oldProperties.find(
              ({ namespace: matchNamespace }) => namespace === matchNamespace
            );
            if (!oldMatch) return true;
            return oldMatch.data !== newData;
          }
        );
        const propertiesToKeep = oldProperties.filter(({ namespace }) => {
          return !propertiesToUpdate.some(
            (newProperty) => newProperty.namespace === namespace
          );
        });

        return {
          ...previousQuote,
          properties: [...propertiesToKeep, ...propertiesToUpdate],
        };
      });
    },
    [setQuote]
  );

  const clearQuote = useCallback(() => {
    setQuote({ id: null, properties: [] });
  }, [setQuote]);

  return (
    <EntitiesContext.Provider
      value={{
        error,
        quote: {
          id: quoteID,
          properties: quote.properties,
          actions: {
            setId: setQuoteId,
            updateProperties: updateQuoteProperties,
            clear: clearQuote,
          },
        },
      }}
    >
      {children}
    </EntitiesContext.Provider>
  );
};

export const useEntities = () => {
  const context = useContext(EntitiesContext);
  if (!context)
    throw new Error('useEntities must be used within an EntitiesProvider');
  return context;
};
